/**
 * Track registry — a set of distinct closed-loop circuits on the y = 0 plane.
 *
 * Coordinate conventions (see src/types.ts):
 *   - Y-up world; the racing surface lies just above y = 0.
 *   - Kart forward = (sin(heading), 0, cos(heading)); heading = atan2(dir.x, dir.z).
 *   - Travel direction along the loop is the curve tangent; start headings use
 *         heading = Math.atan2(tangent.x, tangent.z).
 *
 * A single `buildTrack(spec)` turns a `TrackSpec` (control points + a themed
 * palette + an environment mood) into a fully-dressed circuit: rolling terrain
 * that flattens into the track, textured tarmac with a rubbered-in racing line,
 * raised kerbs, painted edge lines, armco barriers on both sides, a start gantry
 * with grandstands, lit checkpoint gates, instanced trees/rocks/hoardings and a
 * fogged mountain ring.
 *
 * `sample()` is the query the physics step makes every frame; it is backed by a
 * uniform grid over the centerline, so it costs a handful of distance checks
 * rather than a scan of the whole loop.
 *
 * Four tracks are registered (see TRACKS). Add one by writing a TrackSpec and a
 * matching TrackInfo entry.
 */
import * as THREE from "three";

import { TrackData, Checkpoint, RoadSample, StartSlot, TrackEnvironment } from "./types";
import { MAX_PLAYERS, DEFAULT_TRACK_ID as PROTOCOL_DEFAULT_TRACK_ID } from "../shared/protocol";
import { createCheckpointMarkers } from "./checkpoints";
import {
  createAsphaltTextures,
  createCheckerTexture,
  createKerbTexture,
  createTerrainTextures,
  surfaceNoise,
} from "./textures";
import {
  createBroadleaves,
  createConifers,
  createGrandstand,
  createGuardrail,
  createHoardings,
  createMountainRing,
  createRocks,
  createStartGantry,
  type Glow,
  type Placement,
} from "./scenery";

const ROAD_SEGMENTS = 260; // samples around the loop for surface + queries
const CHECKPOINT_COUNT = 12;

/** Height the tarmac sits at. The terrain is pushed below it to avoid z-fighting. */
const ROAD_Y = 0.05;
/** Height of the flattened terrain immediately around the circuit. */
const VERGE_Y = -0.08;

/** Half-extent of the terrain plane, and how finely it is tessellated. */
const TERRAIN_EXTENT = 1000;
const TERRAIN_SEGMENTS = 128;
/** Peak height of the rolling hills outside the circuit. */
const TERRAIN_RELIEF = 26;

/** Width of the paved runoff between kerb and barrier. */
const RUNOFF = 6.5;

// ---------------------------------------------------------------------------
// Public registry types & exports
// ---------------------------------------------------------------------------

/** Lightweight descriptor used by menus / selection UI. */
export interface TrackInfo {
  id: string;
  name: string;
  emoji: string;
  description: string;
  difficulty: 1 | 2 | 3;
}

/** Re-export the canonical default id so callers have one source of truth. */
export const DEFAULT_TRACK_ID: string = PROTOCOL_DEFAULT_TRACK_ID;

// ---------------------------------------------------------------------------
// Internal build spec
// ---------------------------------------------------------------------------

/** Per-track palette. Everything that gives a map its identity lives here. */
interface TrackTheme {
  /** Terrain base and the second tone mottled into it. */
  ground: number;
  groundAccent: number;
  /** Tarmac. */
  road: number;
  /** The two alternating kerb colours. */
  kerbA: number;
  kerbB: number;
  /** Painted line just inside the kerb. */
  edgeLine: number;
  /** Armco ribbon and its posts. */
  barrier: number;
  barrierPost: number;
  /** Gantry beam and banner. */
  structure: number;
  accent: number;
  /** Vegetation. */
  trunk: number;
  foliage: number[];
  /** Boulders and sponsor boards. */
  rocks: number[];
  hoardings: number[];
  /** Silhouette colour for the horizon ring. */
  mountain: number;
  /** Share of vegetation props that are conifers rather than broadleaves. */
  coniferShare: number;
  /** Share of props that are rocks rather than trees. */
  rockShare: number;
  /** Backlighting applied to kerbs, barriers and signage (night circuits). */
  glow: Glow;
}

/** Everything needed to build one track. */
interface TrackSpec {
  id: string;
  /** Closed-loop centerline control points (all y = 0). */
  controlPoints: THREE.Vector3[];
  /** Full road width in world units. */
  roadWidth: number;
  theme: TrackTheme;
  environment: TrackEnvironment;
}

// ---------------------------------------------------------------------------
// Curve sampling helpers
// ---------------------------------------------------------------------------

/** One precomputed centerline sample. */
interface CurveSample {
  point: THREE.Vector3; // on y = 0
  tangent: THREE.Vector3; // unit, on y = 0
  normal: THREE.Vector3; // unit horizontal left/right normal (perp to tangent in XZ)
  /** Arc length from sample 0 to here — used for texture UVs. */
  distance: number;
}

/** Horizontal (XZ) unit normal from a tangent: rotate tangent 90 deg in XZ. */
function horizontalNormal(tangent: THREE.Vector3): THREE.Vector3 {
  // tangent is (tx, 0, tz); a perpendicular vector in XZ is (tz, 0, -tx).
  return new THREE.Vector3(tangent.z, 0, -tangent.x).normalize();
}

/**
 * Uniform grid over the centerline samples. The loop is long and thin, so a
 * bucketed lookup turns "which part of the track am I near?" from a 260-sample
 * scan into a couple of cell probes — and the physics step asks that question
 * for every kart, every frame.
 */
class SampleGrid {
  private static readonly CELL = 20;
  private readonly buckets = new Map<number, number[]>();

  constructor(private readonly samples: CurveSample[]) {
    for (let i = 0; i < samples.length; i++) {
      const p = samples[i].point;
      const key = SampleGrid.key(p.x, p.z);
      const bucket = this.buckets.get(key);
      if (bucket) bucket.push(i);
      else this.buckets.set(key, [i]);
    }
  }

  /** Cell coordinates packed into one integer. Track extents stay well inside. */
  private static key(x: number, z: number): number {
    const cx = Math.floor(x / SampleGrid.CELL) + 512;
    const cz = Math.floor(z / SampleGrid.CELL) + 512;
    return cx * 1024 + cz;
  }

  /**
   * Nearest sample searching cells out to `radius`. The result is only
   * guaranteed exact when its distance is <= radius; callers widen or fall back
   * when it is not.
   */
  private search(x: number, z: number, radius: number): { index: number; dist: number } | null {
    const cells = Math.ceil(radius / SampleGrid.CELL);
    const cx = Math.floor(x / SampleGrid.CELL);
    const cz = Math.floor(z / SampleGrid.CELL);
    let best = -1;
    let bestD2 = Infinity;
    for (let dx = -cells; dx <= cells; dx++) {
      for (let dz = -cells; dz <= cells; dz++) {
        const bucket = this.buckets.get(
          (cx + dx + 512) * 1024 + (cz + dz + 512),
        );
        if (!bucket) continue;
        for (const i of bucket) {
          const p = this.samples[i].point;
          const ex = p.x - x;
          const ez = p.z - z;
          const d2 = ex * ex + ez * ez;
          if (d2 < bestD2) {
            bestD2 = d2;
            best = i;
          }
        }
      }
    }
    return best < 0 ? null : { index: best, dist: Math.sqrt(bestD2) };
  }

  /** Exhaustive fallback for positions far from any bucket. */
  private scanAll(x: number, z: number): { index: number; dist: number } {
    let best = 0;
    let bestD2 = Infinity;
    for (let i = 0; i < this.samples.length; i++) {
      const p = this.samples[i].point;
      const ex = p.x - x;
      const ez = p.z - z;
      const d2 = ex * ex + ez * ez;
      if (d2 < bestD2) {
        bestD2 = d2;
        best = i;
      }
    }
    return { index: best, dist: Math.sqrt(bestD2) };
  }

  /** Exact nearest sample, widening the search until it is provably correct. */
  nearest(x: number, z: number): { index: number; dist: number } {
    for (const radius of [SampleGrid.CELL, SampleGrid.CELL * 4]) {
      const hit = this.search(x, z, radius);
      if (hit && hit.dist <= radius) return hit;
    }
    return this.scanAll(x, z);
  }

  /**
   * Distance to the nearest sample, but only if something lies within `radius`.
   * Used for placement tests and terrain flattening, where "far away" needs no
   * exact answer.
   */
  distanceWithin(x: number, z: number, radius: number): number | null {
    const hit = this.search(x, z, radius);
    return hit && hit.dist <= radius ? hit.dist : null;
  }
}

// ---------------------------------------------------------------------------
// Gate crossing
// ---------------------------------------------------------------------------

const _gateScratch = new THREE.Vector3();

/**
 * Did the step from `from` to `to` pass through checkpoint `cp`?
 *
 * A gate is a line segment across the track, not a circle around the
 * centerline, so this is a segment-vs-plane test rather than a proximity check:
 * the kart must cross the gate's plane travelling forwards, and the crossing
 * point must fall within the gate's half-width.
 *
 * Testing the whole step rather than the current position means no amount of
 * speed can slip through between frames. Requiring the correct direction of
 * travel is what keeps the far side of a hairpin — which crosses the same
 * infinite plane the other way — from triggering it, and the half-width check
 * rejects any parallel section of track that happens to lie beyond the plane.
 */
export function passedGate(cp: Checkpoint, from: THREE.Vector3, to: THREE.Vector3): boolean {
  const before = _gateScratch.copy(from).sub(cp.position).dot(cp.forward);
  const after = _gateScratch.copy(to).sub(cp.position).dot(cp.forward);
  if (before > 0 || after <= 0) return false;

  // Interpolate to the exact crossing point and measure how far off-centre it
  // landed. before !== after is guaranteed here, since before <= 0 < after.
  _gateScratch.copy(from).lerp(to, before / (before - after)).sub(cp.position);
  _gateScratch.y = 0;
  const along = _gateScratch.dot(cp.forward);
  const lateralSq = Math.max(0, _gateScratch.lengthSq() - along * along);
  return lateralSq <= cp.halfWidth * cp.halfWidth;
}

/** Deterministic pseudo-random so every client builds an identical track. */
function seededRandom(seed: number): () => number {
  let state = seed | 0;
  return () => {
    state = (Math.imul(state, 1103515245) + 12345) & 0x7fffffff;
    return state / 0x7fffffff;
  };
}

/** Smooth 0 -> 1 ramp between two edges. */
function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

// ---------------------------------------------------------------------------
// The single track builder — shared geometry for every track.
// ---------------------------------------------------------------------------

function buildTrack(spec: TrackSpec): TrackData {
  const { theme } = spec;
  const ROAD_WIDTH = spec.roadWidth;
  const half = ROAD_WIDTH / 2;
  const wallRadius = half + RUNOFF;
  const rand = seededRandom(1337);

  const group = new THREE.Group();
  group.name = spec.id;

  // -------------------------------------------------------------------------
  // Centerline curve — a smooth closed loop from the spec's control points,
  // sampled at even arc length so texture UVs never stretch through corners.
  // -------------------------------------------------------------------------
  const curve = new THREE.CatmullRomCurve3(spec.controlPoints, true, "catmullrom", 0.5);

  const samples: CurveSample[] = [];
  let running = 0;
  for (let i = 0; i < ROAD_SEGMENTS; i++) {
    const t = i / ROAD_SEGMENTS; // [0, 1); loop, so no duplicate endpoint
    const point = curve.getPointAt(t);
    point.y = 0;
    const tangent = curve.getTangentAt(t);
    tangent.y = 0;
    tangent.normalize();
    if (i > 0) running += point.distanceTo(samples[i - 1].point);
    samples.push({ point, tangent, normal: horizontalNormal(tangent), distance: running });
  }
  const grid = new SampleGrid(samples);

  // -------------------------------------------------------------------------
  // Terrain: a big displaced plane. The relief is faded out as it approaches the
  // circuit so the racing surface stays perfectly flat (the physics is 2D) while
  // the world beyond the barriers rolls.
  // -------------------------------------------------------------------------
  const terrainGeo = new THREE.PlaneGeometry(
    TERRAIN_EXTENT * 2,
    TERRAIN_EXTENT * 2,
    TERRAIN_SEGMENTS,
    TERRAIN_SEGMENTS,
  );
  terrainGeo.rotateX(-Math.PI / 2); // face +Y; x/z are now world axes

  const terrainPos = terrainGeo.getAttribute("position") as THREE.BufferAttribute;
  const flatStart = wallRadius + 3;
  const flatEnd = wallRadius + 46;
  for (let i = 0; i < terrainPos.count; i++) {
    const x = terrainPos.getX(i);
    const z = terrainPos.getZ(i);
    const near = grid.distanceWithin(x, z, flatEnd);
    const openness = near === null ? 1 : smoothstep(flatStart, flatEnd, near);
    if (openness <= 0) {
      terrainPos.setY(i, VERGE_Y);
      continue;
    }
    // Two octaves of large-scale relief plus a finer ripple.
    const u = (x + TERRAIN_EXTENT) / (TERRAIN_EXTENT * 2);
    const v = (z + TERRAIN_EXTENT) / (TERRAIN_EXTENT * 2);
    const hills = surfaceNoise(u, v, 6, 4, 404) - 0.5;
    const ripple = surfaceNoise(u, v, 24, 2, 909) - 0.5;
    terrainPos.setY(i, VERGE_Y + (hills * 2 + ripple * 0.45) * TERRAIN_RELIEF * openness);
  }
  terrainGeo.computeVertexNormals();

  const terrainTex = createTerrainTextures(theme.ground, theme.groundAccent);
  for (const tex of [terrainTex.map, terrainTex.normalMap, terrainTex.roughnessMap]) {
    tex.repeat.set(64, 64);
  }
  const terrain = new THREE.Mesh(
    terrainGeo,
    new THREE.MeshStandardMaterial({
      map: terrainTex.map,
      normalMap: terrainTex.normalMap,
      roughnessMap: terrainTex.roughnessMap,
      normalScale: new THREE.Vector2(0.7, 0.7),
      metalness: 0,
    }),
  );
  terrain.receiveShadow = true;
  terrain.name = "terrain";
  group.add(terrain);

  // -------------------------------------------------------------------------
  // Ribbon helper: build a closed strip between two lateral offsets, with UVs
  // running across (u) and along (v, in world units / `vScale`).
  // -------------------------------------------------------------------------
  const buildRibbon = (
    innerOffset: number,
    outerOffset: number,
    innerY: number,
    outerY: number,
    vScale: number,
  ): THREE.BufferGeometry => {
    const positions: number[] = [];
    const normals: number[] = [];
    const uvs: number[] = [];
    const indices: number[] = [];

    for (let i = 0; i < ROAD_SEGMENTS; i++) {
      const s = samples[i];
      positions.push(
        s.point.x + s.normal.x * innerOffset, innerY, s.point.z + s.normal.z * innerOffset,
        s.point.x + s.normal.x * outerOffset, outerY, s.point.z + s.normal.z * outerOffset,
      );
      normals.push(0, 1, 0, 0, 1, 0);
      const v = s.distance / vScale;
      uvs.push(0, v, 1, v);
    }
    for (let i = 0; i < ROAD_SEGMENTS; i++) {
      const a = (i * 2) % (ROAD_SEGMENTS * 2);
      const b = (i * 2 + 1) % (ROAD_SEGMENTS * 2);
      const c = ((i + 1) * 2) % (ROAD_SEGMENTS * 2);
      const d = ((i + 1) * 2 + 1) % (ROAD_SEGMENTS * 2);
      indices.push(a, c, b, b, c, d);
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
    geo.setAttribute("normal", new THREE.Float32BufferAttribute(normals, 3));
    geo.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
    geo.setIndex(indices);
    geo.computeVertexNormals();
    geo.computeBoundingSphere();
    return geo;
  };

  // -------------------------------------------------------------------------
  // Road surface. UVs are arc-length based, so one texture tile is exactly one
  // road-width square however tightly the loop turns.
  // -------------------------------------------------------------------------
  const asphalt = createAsphaltTextures(theme.road);
  const road = new THREE.Mesh(
    buildRibbon(half, -half, ROAD_Y, ROAD_Y, ROAD_WIDTH),
    new THREE.MeshStandardMaterial({
      map: asphalt.map,
      normalMap: asphalt.normalMap,
      roughnessMap: asphalt.roughnessMap,
      normalScale: new THREE.Vector2(0.55, 0.55),
      metalness: 0.02,
    }),
  );
  road.receiveShadow = true;
  road.name = "road";
  group.add(road);

  // Paved runoff either side: same tarmac, dulled down, between kerb and armco.
  const runoffMat = new THREE.MeshStandardMaterial({
    map: asphalt.map,
    normalMap: asphalt.normalMap,
    roughnessMap: asphalt.roughnessMap,
    color: 0x767676,
    normalScale: new THREE.Vector2(0.5, 0.5),
    metalness: 0,
  });
  for (const side of [1, -1]) {
    const strip = new THREE.Mesh(
      buildRibbon(side * (half + 1.4), side * wallRadius, ROAD_Y - 0.02, VERGE_Y + 0.02, ROAD_WIDTH),
      runoffMat,
    );
    strip.receiveShadow = true;
    strip.name = `runoff-${side}`;
    group.add(strip);
  }

  // Painted edge line just inside each kerb.
  const edgeMat = new THREE.MeshStandardMaterial({
    color: theme.edgeLine,
    roughness: 0.55,
    emissive: new THREE.Color(theme.glow.color),
    emissiveIntensity: theme.glow.intensity * 0.7,
  });
  for (const side of [1, -1]) {
    const line = new THREE.Mesh(
      buildRibbon(side * (half - 0.15), side * (half - 0.65), ROAD_Y + 0.01, ROAD_Y + 0.01, ROAD_WIDTH),
      edgeMat,
    );
    line.name = `edge-${side}`;
    group.add(line);
  }

  // Raised kerbs: a shallow ramp from the road edge outward, striped along its
  // length. Two meshes for the whole circuit instead of a few hundred boxes.
  const kerbTex = createKerbTexture(theme.kerbA, theme.kerbB);
  const kerbMat = new THREE.MeshStandardMaterial({
    map: kerbTex,
    roughness: 0.6,
    emissiveMap: kerbTex,
    emissive: new THREE.Color(theme.glow.color),
    emissiveIntensity: theme.glow.intensity,
  });
  for (const side of [1, -1]) {
    const kerb = new THREE.Mesh(
      buildRibbon(side * half, side * (half + 1.4), ROAD_Y, ROAD_Y + 0.17, 2.4),
      kerbMat,
    );
    kerb.receiveShadow = true;
    kerb.name = `kerb-${side}`;
    group.add(kerb);
  }

  // -------------------------------------------------------------------------
  // Barriers on both sides, exactly where the physics wall stands.
  // -------------------------------------------------------------------------
  for (const side of [1, -1]) {
    const path = samples.map((s) =>
      new THREE.Vector3(
        s.point.x + s.normal.x * side * wallRadius,
        0,
        s.point.z + s.normal.z * side * wallRadius,
      ),
    );
    group.add(createGuardrail(path, theme.barrier, theme.barrierPost, theme.glow));
  }

  // -------------------------------------------------------------------------
  // Start / finish: painted chequers across the road, a gantry over it, and a
  // grandstand on whichever side has room.
  // -------------------------------------------------------------------------
  const startSample = samples[0];
  const startYaw = Math.atan2(startSample.tangent.x, startSample.tangent.z);

  const checker = createCheckerTexture(8);
  checker.repeat.set(ROAD_WIDTH / 1.6, 2);
  const startLine = new THREE.Mesh(
    new THREE.PlaneGeometry(ROAD_WIDTH, 3.2),
    new THREE.MeshStandardMaterial({ map: checker, roughness: 0.75 }),
  );
  startLine.rotation.set(-Math.PI / 2, 0, -startYaw);
  startLine.position.set(startSample.point.x, ROAD_Y + 0.012, startSample.point.z);
  startLine.name = "startFinish";
  group.add(startLine);

  const gantry = createStartGantry(ROAD_WIDTH, theme.accent, theme.structure);
  gantry.position.set(startSample.point.x, VERGE_Y, startSample.point.z);
  gantry.rotation.y = startYaw;
  group.add(gantry);

  for (const side of [1, -1]) {
    const dist = wallRadius + 16;
    const x = startSample.point.x + startSample.normal.x * side * dist;
    const z = startSample.point.z + startSample.normal.z * side * dist;
    // Skip the side where another part of the loop runs too close to fit a stand.
    if ((grid.distanceWithin(x, z, dist - 2) ?? Infinity) < dist - 2) continue;
    const stand = createGrandstand(theme.structure, theme.accent, rand);
    stand.position.set(x, VERGE_Y, z);
    // Seating rises toward local +Z, so spectators face local -Z: aim that at
    // the track by pointing local +Z along the outward normal.
    stand.rotation.y = Math.atan2(startSample.normal.x * side, startSample.normal.z * side);
    group.add(stand);
  }

  // -------------------------------------------------------------------------
  // Scenery beyond the barriers. Candidates are rejected if they land near ANY
  // part of the loop, so nothing sprouts inside a hairpin.
  // -------------------------------------------------------------------------
  const conifers: Placement[] = [];
  const broadleaves: Placement[] = [];
  const rocks: Placement[] = [];
  const hoardings: Placement[] = [];

  const CANDIDATES = 460;
  const clearance = wallRadius + 5;
  for (let k = 0; k < CANDIDATES; k++) {
    const s = samples[Math.floor(rand() * ROAD_SEGMENTS)];
    const side = rand() < 0.5 ? 1 : -1;
    const dist = clearance + rand() * 78;
    const x = s.point.x + s.normal.x * side * dist;
    const z = s.point.z + s.normal.z * side * dist;
    if ((grid.distanceWithin(x, z, clearance) ?? Infinity) < clearance) continue;

    const roll = rand();
    if (roll < 0.14 && dist < clearance + 14) {
      // Sponsor boards hug the barrier. The panel is modelled wide along its
      // local X, so yaw it a quarter turn past the tangent to lay that width
      // along the track and point its face at the racing line.
      hoardings.push({
        x,
        z,
        scale: 0.85 + rand() * 0.4,
        rotation: Math.atan2(s.tangent.x, s.tangent.z) + Math.PI / 2,
      });
    } else if (roll < 0.14 + theme.rockShare) {
      rocks.push({ x, z, scale: 1.6 + rand() * 4.2, rotation: rand() * Math.PI * 2 });
    } else if (rand() < theme.coniferShare) {
      conifers.push({ x, z, scale: 0.7 + rand() * 0.8, rotation: rand() * Math.PI * 2 });
    } else {
      broadleaves.push({ x, z, scale: 0.75 + rand() * 0.75, rotation: rand() * Math.PI * 2 });
    }
  }

  const scenery = new THREE.Group();
  scenery.name = "scenery";
  scenery.add(createConifers(conifers, theme.trunk, theme.foliage, theme.glow, rand));
  scenery.add(createBroadleaves(broadleaves, theme.trunk, theme.foliage, theme.glow, rand));
  scenery.add(createRocks(rocks, theme.rocks, rand));
  scenery.add(createHoardings(hoardings, theme.hoardings, theme.glow, rand));
  group.add(scenery);

  // Horizon ring, centred on the circuit so it surrounds the whole map.
  const bounds = new THREE.Box3().setFromPoints(samples.map((s) => s.point));
  const center = bounds.getCenter(new THREE.Vector3());
  const mountains = createMountainRing(880, theme.mountain, 26, rand);
  mountains.position.set(center.x, 0, center.z);
  group.add(mountains);

  // -------------------------------------------------------------------------
  // Checkpoints — evenly spaced gates by arc length. Index 0 is the start line.
  // -------------------------------------------------------------------------
  const checkpoints: Checkpoint[] = [];
  for (let c = 0; c < CHECKPOINT_COUNT; c++) {
    const idx = Math.round((c / CHECKPOINT_COUNT) * ROAD_SEGMENTS) % ROAD_SEGMENTS;
    const s = samples[idx];
    checkpoints.push({
      index: c,
      position: new THREE.Vector3(s.point.x, 0, s.point.z),
      // Reach past the barriers, so anywhere a kart can physically drive counts
      // as passing the gate.
      halfWidth: wallRadius + 2,
      forward: s.tangent.clone(),
    });
  }

  // Gates are a plane test, which is invisible; give every one of them a lit
  // arch so the driver can see what the lap counter is measuring.
  const checkpointMarkers = createCheckpointMarkers(checkpoints, wallRadius);
  group.add(checkpointMarkers.group);

  // -------------------------------------------------------------------------
  // Start positions — staggered 2-column grid BEHIND the start line, facing
  // ALONG travel direction. Offset back along -tangent from t = 0.
  // -------------------------------------------------------------------------
  const startPositions: StartSlot[] = [];
  const rowGap = 4; // units between rows
  const colOffset = ROAD_WIDTH * 0.22; // left/right offset within the road
  const firstBack = 6; // start the grid a bit behind the line

  const slotCount = Math.max(MAX_PLAYERS, 8);
  for (let i = 0; i < slotCount; i++) {
    const rowIndex = Math.floor(i / 2); // 0,0,1,1,2,2...
    const col = i % 2 === 0 ? -1 : 1; // left / right column
    const back = firstBack + rowIndex * rowGap;
    const pos = new THREE.Vector3()
      .copy(startSample.point)
      .addScaledVector(startSample.tangent, -back) // behind the line
      .addScaledVector(startSample.normal, col * colOffset);
    pos.y = 0;
    startPositions.push({ position: pos, heading: startYaw });
  }

  // -------------------------------------------------------------------------
  // Centerline for the minimap — ordered, cloned sample points on y = 0.
  // -------------------------------------------------------------------------
  const centerline: THREE.Vector3[] = samples.map((s) => new THREE.Vector3(s.point.x, 0, s.point.z));

  // -------------------------------------------------------------------------
  // sample(pos): grid-accelerated nearest-centerline query.
  // -------------------------------------------------------------------------
  const sample = (pos: THREE.Vector3): RoadSample => {
    const hit = grid.nearest(pos.x, pos.z);
    const s = samples[hit.index];
    return {
      point: new THREE.Vector3(s.point.x, 0, s.point.z),
      forward: s.tangent.clone(),
      distance: hit.dist,
      onRoad: hit.dist <= half,
    };
  };

  return {
    group,
    checkpoints,
    checkpointMarkers,
    startPositions,
    roadWidth: ROAD_WIDTH,
    wallRadius,
    centerline,
    environment: spec.environment,
    sample,
  };
}

// ---------------------------------------------------------------------------
// Track specifications — each has DISTINCT control points, palette and mood.
// ---------------------------------------------------------------------------

/**
 * 1. Sunset Loop (default) — the original flowing loop, run at golden hour.
 *    Warm savanna, long shadows, easy wide road.
 */
const SUNSET_LOOP: TrackSpec = {
  id: "sunset-loop",
  roadWidth: 16,
  controlPoints: [
    new THREE.Vector3(0, 0, 150),
    new THREE.Vector3(120, 0, 120),
    new THREE.Vector3(170, 0, 20),
    new THREE.Vector3(120, 0, -80),
    new THREE.Vector3(40, 0, -60),
    new THREE.Vector3(10, 0, -140),
    new THREE.Vector3(-90, 0, -160),
    new THREE.Vector3(-160, 0, -60),
    new THREE.Vector3(-120, 0, 60),
    new THREE.Vector3(-50, 0, 110),
  ],
  theme: {
    ground: 0x6d8438,
    groundAccent: 0x9aa34a,
    road: 0x33333a,
    kerbA: 0xf5f5f5,
    kerbB: 0xd42a2a,
    edgeLine: 0xf0f0e8,
    barrier: 0xd8dde2,
    barrierPost: 0x6a6f75,
    structure: 0xc8ccd2,
    accent: 0xffb703,
    trunk: 0x6b4a2b,
    foliage: [0x2f8f3a, 0x39a94a, 0x2a7d33, 0x4f9c34],
    rocks: [0x8d8677, 0x9c9484, 0x7b756a],
    hoardings: [0xffb703, 0xfb8500, 0x8ecae6, 0x219ebc, 0xe76f51],
    mountain: 0x5c6b7a,
    coniferShare: 0.35,
    rockShare: 0.14,
    glow: { color: 0x000000, intensity: 0 },
  },
  environment: {
    skyTop: 0x1f5fc4,
    skyHorizon: 0xffb277,
    skyGround: 0x6b5747,
    sunDirection: new THREE.Vector3(-0.62, 0.24, -0.75),
    sunColor: 0xffd6a0,
    sunIntensity: 3.1,
    hemiSky: 0xffd3a8,
    hemiGround: 0x54602c,
    hemiIntensity: 0.75,
    ambientIntensity: 0.16,
    fogColor: 0xffbe8c,
    fogDensity: 0.0013,
    exposure: 1.0,
    bloom: 0.5,
    dustColor: 0xcbb98d,
    sunVisible: true,
  },
};

/**
 * 2. Canyon Dash — twistier, with a couple of tight turns / hairpins. Red rock
 *    under a hazy desert noon; narrow (hard) road.
 */
const CANYON_DASH: TrackSpec = {
  id: "canyon-dash",
  roadWidth: 13,
  controlPoints: [
    new THREE.Vector3(0, 0, 140),
    new THREE.Vector3(70, 0, 150),
    new THREE.Vector3(95, 0, 80),
    new THREE.Vector3(50, 0, 55), // pinch in for a tight kink
    new THREE.Vector3(120, 0, 10),
    new THREE.Vector3(150, 0, -90),
    new THREE.Vector3(70, 0, -110),
    new THREE.Vector3(90, 0, -170), // hairpin out
    new THREE.Vector3(-10, 0, -180),
    new THREE.Vector3(-30, 0, -100),
    new THREE.Vector3(-120, 0, -120),
    new THREE.Vector3(-150, 0, -20),
    new THREE.Vector3(-80, 0, 30), // tight inside loop
    new THREE.Vector3(-120, 0, 90),
    new THREE.Vector3(-60, 0, 130),
  ],
  theme: {
    ground: 0xb9743f,
    groundAccent: 0xd9a066,
    road: 0x4f4137,
    kerbA: 0xf2e3c0,
    kerbB: 0x8a2f1a,
    edgeLine: 0xf4ead2,
    barrier: 0xc9bda8,
    barrierPost: 0x6d5c4a,
    structure: 0xcbb99c,
    accent: 0xe07a3c,
    trunk: 0x5a3a22,
    foliage: [0x7a7a3f, 0x8a8a45, 0x6a6a35],
    rocks: [0x9e3b1f, 0xc25a2b, 0x7a2f18, 0xd98a4a, 0x6b3a22],
    hoardings: [0xe07a3c, 0xd94f2b, 0xf0c05a, 0x9c5a2f],
    mountain: 0x8a5a3c,
    coniferShare: 0.1,
    rockShare: 0.6,
    glow: { color: 0x000000, intensity: 0 },
  },
  environment: {
    skyTop: 0x2c7ad6,
    skyHorizon: 0xe6c79c,
    skyGround: 0x8a6a4a,
    sunDirection: new THREE.Vector3(0.42, 0.78, 0.28),
    sunColor: 0xfff2d8,
    sunIntensity: 3.5,
    hemiSky: 0xd9e8ff,
    hemiGround: 0x8a5a34,
    hemiIntensity: 0.65,
    ambientIntensity: 0.2,
    fogColor: 0xdec19c,
    fogDensity: 0.0016,
    exposure: 1.05,
    bloom: 0.32,
    dustColor: 0xd9a870,
    sunVisible: true,
  },
};

/**
 * 3. Neon Circuit — a sleek, angular night circuit. Everything trackside is
 *    backlit, and the bloom pass is turned right up.
 */
const NEON_CIRCUIT: TrackSpec = {
  id: "neon-circuit",
  roadWidth: 15,
  controlPoints: [
    new THREE.Vector3(0, 0, 160),
    new THREE.Vector3(140, 0, 150),
    new THREE.Vector3(160, 0, 40),
    new THREE.Vector3(80, 0, 20),
    new THREE.Vector3(150, 0, -70),
    new THREE.Vector3(60, 0, -150),
    new THREE.Vector3(-40, 0, -120),
    new THREE.Vector3(-60, 0, -180),
    new THREE.Vector3(-150, 0, -140),
    new THREE.Vector3(-120, 0, -30),
    new THREE.Vector3(-180, 0, 60),
    new THREE.Vector3(-70, 0, 90),
  ],
  theme: {
    ground: 0x0d0d1a,
    groundAccent: 0x161630,
    road: 0x17171f,
    kerbA: 0x00e5ff,
    kerbB: 0xff2bd6,
    edgeLine: 0x9ef7ff,
    barrier: 0x2a2a44,
    barrierPost: 0x15152a,
    structure: 0x232338,
    accent: 0x00e5ff,
    trunk: 0x1a1a2a,
    foliage: [0x0e3a4a, 0x123a5a, 0x0e2f4a],
    rocks: [0x1a1a2e, 0x232338, 0x14142a],
    hoardings: [0x00e5ff, 0xff2bd6, 0x7cff00, 0xffe600, 0xb14bff],
    mountain: 0x101024,
    coniferShare: 0.6,
    rockShare: 0.25,
    glow: { color: 0x35e8ff, intensity: 1.4 },
  },
  environment: {
    skyTop: 0x04050d,
    skyHorizon: 0x1d1246,
    skyGround: 0x06060e,
    sunDirection: new THREE.Vector3(0.3, 0.55, -0.7),
    sunColor: 0x9fb4ff,
    sunIntensity: 0.6,
    hemiSky: 0x2b3a8c,
    hemiGround: 0x130a24,
    hemiIntensity: 0.55,
    ambientIntensity: 0.26,
    fogColor: 0x0d0a22,
    fogDensity: 0.0018,
    exposure: 1.25,
    bloom: 1.15,
    dustColor: 0x6a6a8a,
    sunVisible: false,
  },
};

/**
 * 4. Frost Bowl — big, wide, fast sweeping loop under flat overcast light.
 *    Extra-wide road; fast and slippery.
 */
const FROST_BOWL: TrackSpec = {
  id: "frost-bowl",
  roadWidth: 18,
  controlPoints: [
    new THREE.Vector3(0, 0, 190),
    new THREE.Vector3(150, 0, 150),
    new THREE.Vector3(200, 0, 30),
    new THREE.Vector3(160, 0, -120),
    new THREE.Vector3(40, 0, -180),
    new THREE.Vector3(-110, 0, -170),
    new THREE.Vector3(-200, 0, -50),
    new THREE.Vector3(-180, 0, 100),
    new THREE.Vector3(-60, 0, 170),
  ],
  theme: {
    ground: 0xe3ecf5,
    groundAccent: 0xc6d8ea,
    road: 0x8ea5be,
    kerbA: 0xffffff,
    kerbB: 0x3f7cc0,
    edgeLine: 0xffffff,
    barrier: 0xdfe8f2,
    barrierPost: 0x7d8b9a,
    structure: 0xcfdae6,
    accent: 0x4a86c8,
    trunk: 0x5f5044,
    foliage: [0x27543f, 0x2f6a4c, 0x1f4636],
    rocks: [0xe6eef7, 0xc9dcec, 0xaec8de],
    hoardings: [0x4a86c8, 0x8fc0e8, 0xffffff, 0x2f5f96],
    mountain: 0x9fb4c8,
    coniferShare: 0.9,
    rockShare: 0.2,
    glow: { color: 0x000000, intensity: 0 },
  },
  environment: {
    skyTop: 0x9dbfe8,
    skyHorizon: 0xeaf2fb,
    skyGround: 0xccd9e6,
    sunDirection: new THREE.Vector3(0.28, 0.6, 0.5),
    sunColor: 0xfff6ec,
    sunIntensity: 1.9,
    hemiSky: 0xe2eeff,
    hemiGround: 0xb6c7d6,
    hemiIntensity: 1.0,
    ambientIntensity: 0.32,
    fogColor: 0xdfeaf6,
    fogDensity: 0.0019,
    exposure: 1.05,
    bloom: 0.38,
    dustColor: 0xf2f7ff,
    sunVisible: true,
  },
};

// ---------------------------------------------------------------------------
// Registry — order matters. TRACKS[0].id === DEFAULT_TRACK_ID ("sunset-loop").
// ---------------------------------------------------------------------------

const SPECS: Record<string, TrackSpec> = {
  [SUNSET_LOOP.id]: SUNSET_LOOP,
  [CANYON_DASH.id]: CANYON_DASH,
  [NEON_CIRCUIT.id]: NEON_CIRCUIT,
  [FROST_BOWL.id]: FROST_BOWL,
};

/** Public track descriptors, in menu order. TRACKS[0] is the default track. */
export const TRACKS: TrackInfo[] = [
  {
    id: "sunset-loop",
    emoji: "🌅",
    name: "Sunset Loop",
    description: "Sweeping golden-hour curves through the savanna.",
    difficulty: 1,
  },
  {
    id: "canyon-dash",
    emoji: "🏜️",
    name: "Canyon Dash",
    description: "Tight technical hairpins through red rock.",
    difficulty: 3,
  },
  {
    id: "neon-circuit",
    emoji: "🌃",
    name: "Neon Circuit",
    description: "A backlit night circuit that glows in the dark.",
    difficulty: 2,
  },
  {
    id: "frost-bowl",
    emoji: "❄️",
    name: "Frost Bowl",
    description: "Wide, fast, slippery sweeps over snow.",
    difficulty: 2,
  },
];

/** Builds the named track. An unknown id falls back to the default track. */
export function createTrack(id: string): TrackData {
  const spec = SPECS[id] ?? SPECS[DEFAULT_TRACK_ID] ?? SUNSET_LOOP;
  return buildTrack(spec);
}
